'use client'

import dynamic from 'next/dynamic'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { alpha } from '@mui/material/styles'
import { AppBar, Toolbar, Container, Grid, Box, IconButton, Divider, Backdrop, Slide, Portal, useMediaQuery, useTheme, Button } from '@mui/material'

// Types
import type { MainAppBarProps } from '@/types/components/organisms/app-bar/main'

// Configurations
import Config from '@/config'

// Components
const ListItemMolecule = dynamic(() => import('@/components/molecules/item/list'))
const TextButtonAtom = dynamic(() => import('@/components/atoms/button/text'))
const PrimaryButtonAtom = dynamic(() => import('@/components/atoms/button/primary'))
const RIconAtom = dynamic(() => import('@/components/atoms/icon/r'))
const MenuIconAtom = dynamic(() => import('@/components/atoms/icon/bulk/menu'))
const CloseIconAtom = dynamic(() => import('@/components/atoms/icon/bulk/close'))
const SolutionsMegaMenuOrganism = dynamic(() => import('@/components/organisms/mega-menu/solutions'))
const NavDropdownOrganism = dynamic(() => import('@/components/organisms/mega-menu/nav-dropdown'))

// Plain "Login" button styled like the header links (the colored demo button sits next to it)
const plainButtonSx = {
	color: 'text.primary',
	fontSize: 15,
	fontWeight: 600,
	textTransform: 'inherit',
	px: 2,
	py: 0.75,
	borderRadius: 2,
	transition: 'background-color .2s, color .2s',
	'&:hover': { bgcolor: '#f5f5f7', color: 'grey.600' }
} as const

const MainAppBarOrganism = (props: MainAppBarProps) => {
	// Props
	const { lng, locale } = props

	// Variables
	const theme = useTheme()
	const pathname = usePathname()
	const isMobile = useMediaQuery(theme.breakpoints.down('md'))
	const appBarRef = useRef<HTMLDivElement | null>(null)
	const [open, setOpen] = useState(false)
	const [appBarBottom, setAppBarBottom] = useState<number>(0)
	const navDropdowns = locale.common.navDropdowns
	// Top-level links from locales/<lng>/content.json (the rest are in the Resources / Our Story dropdowns)
	const topMenus = locale.content.menu.map(menu => ({ title: menu.title, link_value: menu.href, type_name: menu.href.startsWith('http') ? 'external' : 'basic' }))

	// Callbacks
	const toggleMenu = useCallback(() => setOpen(!open), [open])

	const setBodyHasColor = useCallback(
		(pathname: string) => {
			const bodyHasColor = pathname === `/${lng}` || pathname === `/${lng}/about-us` || pathname === `/${lng}/contact-us`

			if (typeof document !== 'undefined') {
				if (bodyHasColor) document.body.style.backgroundColor = '#e9ecef'
				else document.body.style.backgroundColor = 'inherit'
			}
		},
		[lng]
	)

	useEffect(() => {
		if (!appBarRef.current) return

		const el = appBarRef.current

		const update = () => {
			const rect = el.getBoundingClientRect()
			setAppBarBottom(Math.round(rect.bottom))
		}

		update()

		const ro = new ResizeObserver(() => update())
		ro.observe(el)

		window.addEventListener('resize', update)

		return () => {
			ro.disconnect()
			window.removeEventListener('resize', update)
		}
	}, [])

	useEffect(() => {
		setBodyHasColor(pathname)
	}, [pathname, setBodyHasColor])

	return (
		<AppBar
			ref={appBarRef}
			position="fixed"
			sx={{
				color: 'inherit',
				// Gray-tinted glass with a strong blur, the same on phones and desktop
				bgcolor: alpha('#e9ecef', 0.72),
				backdropFilter: 'blur(24px) saturate(180%)',
				WebkitBackdropFilter: 'blur(24px) saturate(180%)',
				boxShadow: 'none',
				zIndex: t => t.zIndex.appBar
			}}
		>
			<Toolbar sx={{ minHeight: 60, mt: 2, py: 1, '&.MuiToolbar-root': { px: 0 } }}>
				<Container maxWidth="lg">
					{isMobile ? (
						<Grid
							container
							sx={{
								justifyContent: 'space-between',
								alignItems: 'center',
								wrap: 'nowrap'
							}}
						>
							<Grid>
								<IconButton aria-label={open ? locale.common.aria.closeMenu : locale.common.aria.openMenu} onClick={toggleMenu} edge="start">
									{open ? <CloseIconAtom /> : <MenuIconAtom />}
								</IconButton>
							</Grid>

							<Grid sx={{ display: 'flex' }}>
								<Grid sx={{ '& .MuiButton-root': { fontSize: { xs: 16, md: 18 }, fontWeight: 600 } }}>
									<Link href={`/${lng}`} onClick={toggleMenu}>
										<TextButtonAtom text={locale.common.app.name} size="large" startIcon={<RIconAtom />} />
									</Link>
								</Grid>
							</Grid>
						</Grid>
					) : (
						<Grid container sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
							<Grid size="auto">
								<Grid container sx={{ alignItems: 'center' }}>
									<Grid sx={{ '& .MuiButton-root': { fontSize: { xs: 16, md: 18 }, fontWeight: 600 } }}>
										<Link href={`/${lng}`}>
											<TextButtonAtom text={locale.common.app.name} size="large" startIcon={<RIconAtom />} />
										</Link>
									</Grid>

									<Grid>
										<SolutionsMegaMenuOrganism lng={lng} locale={locale} top={appBarBottom} variant="desktop" />
									</Grid>

									<Grid
										sx={{
											'& .MuiList-root': {
												flexDirection: 'row',
												'& li': {
													width: 'auto',
													'& a .MuiButtonBase-root': {
														p: 0,
														borderRadius: 2,
														transition: 'background-color .2s',
														'&:hover': { bgcolor: '#f5f5f7', '& .MuiTypography-root': { color: 'grey.600' } }
													},
													'& a .MuiButtonBase-root .MuiTypography-root': {
														fontSize: { xs: 12, sm: 15 },
														px: 1.5,
														py: 1,
														transition: 'color .2s',
														display: '-webkit-box',
														WebkitLineClamp: 1,
														WebkitBoxOrient: 'vertical',
														textOverflow: 'ellipsis',
														overflow: 'hidden'
													}
												}
											}
										}}
									>
										<ListItemMolecule baseUrl={`/${lng}`} items={topMenus} />
									</Grid>

									{navDropdowns.map(group => (
										<Grid key={group.title}>
											<NavDropdownOrganism lng={lng} group={group} top={appBarBottom} variant="desktop" />
										</Grid>
									))}
								</Grid>
							</Grid>

							<Grid size="auto" sx={{ display: 'flex' }}>
								<Grid sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
									<Link href={Config.appUrl}>
										<Button sx={plainButtonSx}>{locale.links.login}</Button>
									</Link>

									<Link href={`/${lng}/signup`}>
										<PrimaryButtonAtom text={locale.links.requestDemo} color="primary" size="small" sx={{ px: 2.5, fontSize: 15 }} />
									</Link>
								</Grid>
							</Grid>
						</Grid>
					)}
				</Container>
			</Toolbar>

			{isMobile && (
				<Portal>
					<Backdrop
						open={open}
						onClick={toggleMenu}
						sx={{
							zIndex: t => t.zIndex.appBar - 1,
							bgcolor: alpha('#9B9BAF', 0.1),
							top: `${appBarBottom}px`,
							height: `calc(100dvh - ${appBarBottom}px)`
						}}
					/>

					<Slide in={open} direction="down" timeout={{ enter: 720, exit: 420 }} mountOnEnter unmountOnExit>
						<Box
							role="dialog"
							aria-label={locale.common.aria.mobileMenu}
							onClick={e => {
								// Keep clicks inside from closing via Backdrop
								e.stopPropagation()

								// Close menu when a link inside the panel is clicked
								const target = e.target as HTMLElement
								const anchor = target.closest('a')

								if (anchor) toggleMenu()
							}}
							sx={{
								position: 'fixed',
								zIndex: t => t.zIndex.appBar - 1,
								top: `${appBarBottom}px`,
								left: 0,
								right: 0,
								height: `calc(100dvh - ${appBarBottom}px)`,
								// Same gray glass as the header, a little more solid so the menu reads well over the page
								bgcolor: alpha('#e9ecef', 0.94),
								backdropFilter: 'blur(24px) saturate(180%)',
								WebkitBackdropFilter: 'blur(24px) saturate(180%)',
								overflowY: 'auto'
							}}
						>
							<Container maxWidth="lg" sx={{ py: 2 }}>
								<Divider sx={{ mb: 2 }} />

								<SolutionsMegaMenuOrganism lng={lng} locale={locale} top={appBarBottom} variant="mobile" />

								<Box
									sx={{
										'& .MuiList-root': {
											flexDirection: 'column',
											gap: 1,
											'& li': { width: '100%' }
										}
									}}
								>
									<ListItemMolecule baseUrl={`/${lng}`} items={topMenus} />
								</Box>

								{navDropdowns.map(group => (
									<NavDropdownOrganism key={group.title} lng={lng} group={group} top={appBarBottom} variant="mobile" />
								))}

								<Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1, '& .MuiButton-root': { width: '100%' } }}>
									<Link href={`/${lng}/signup`} onClick={toggleMenu}>
										<PrimaryButtonAtom text={locale.links.requestDemo} color="primary" size="large" />
									</Link>

									<Link href={Config.appUrl} onClick={toggleMenu}>
										<Button sx={{ ...plainButtonSx, py: 1.5, fontSize: 16 }}>{locale.links.login}</Button>
									</Link>
								</Box>
							</Container>
						</Box>
					</Slide>
				</Portal>
			)}
		</AppBar>
	)
}

export default MainAppBarOrganism
